// People — SPEC-001 §12: roster across visible clinics, instant client-side
// search (INV-1: search filters in memory, zero network calls per keystroke),
// and a profile view backed by GET /api/people/:id.
//
// `employee_secure` fields (SSN, bank/routing/account) never appear here —
// the API this page calls never returns them (SPEC-001 §7, R-4), and this
// file does not reference, request, or reserve a place for them.
import type { JSX } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from '../components/SortableTh';
import { authedGet, authedWrite, ApiError } from '../lib/apiClient';
import { FaultState, EmptyState } from '../components/FaultState';
import { OpenFolderLink } from '../components/OpenFolderLink';
import { ClinicTabs, visibleClinics, activeClinicFrom, MANAGEMENT_TAB_ID, canSeeManagementTab } from '../components/ClinicTabs';
import { PeopleManagement } from '../components/PeopleManagement';
import { PayHours, PayHoursClinicSwitcher } from '../components/PayHours';
import { ServiceCompliance } from '../components/ServiceCompliance';
import { CompanyHrPane } from './Company';
import type { LocationCode } from '../lib/locations';
import { FavoriteStar } from '../components/FavoriteStar';
import { useWrite } from '../components/MoneyForms';
import { formatDate } from '../lib/date';
import { compareByLastName, lastNameKey } from '../lib/personName';
import { formatNoteStamp } from '../lib/clinicDay';
import { birthdaySoon } from '../lib/birthday';
import { NoteMark, NotePanel } from '../components/NotePanel';
import { useSparkleOnView } from '../lib/sparkleOnView';
import { session } from '../lib/session';
import { withGoogleAccount } from '../components/Nav';
import { canSeeNewHires } from '../components/NeedsHireDetailsAlert';
import { useResource, refetchResource, persistResource, peekResource, type Load } from '../lib/store';
import { uploadPersonNhp } from '../lib/nhpFile';
import { roleBucket, ROLE_BUCKET_TITLE } from '../lib/roleBuckets';

const PAY_HOURS_TAB_ID = 'PAY_HOURS';
const PAY_HOURS_HASH_RE = /^#pay-hours(-([A-Z]{4}))?$/;
// HR and SPC moved here from Company (owner, 2026-10-06: "id like to move HR
// and the SPC as a menu item in People"). Same content, same components, and
// the same breadth as on Company — every role that opens People. `#hr` and
// `#spc` open them; Company's old `#hr` / `#compliance` links are sent here
// by lib/router.ts legacyRedirect.
const HR_TAB_ID = 'HR';
const SPC_TAB_ID = 'SPC';

function tabFromHash(hash: string): string | null {
  if (PAY_HOURS_HASH_RE.test(hash)) return PAY_HOURS_TAB_ID;
  if (hash === '#hr') return HR_TAB_ID;
  if (hash === '#spc') return SPC_TAB_ID;
  return null;
}

/** SPC with its own clinic switcher — the extra tab replaces the roster's
 * clinic strip as the active tab, so it carries its own (Pay & Hours does
 * the same, and for the same reason uses the same switcher). */
function PeopleSpc({ initialClinic }: { initialClinic: LocationCode }): JSX.Element {
  const clinics = visibleClinics();
  const [clinic, setClinic] = useState<LocationCode>(() => activeClinicFrom(initialClinic, clinics));
  return (
    <div data-testid="people-spc">
      <PayHoursClinicSwitcher clinics={clinics} active={clinic} onSelect={setClinic} />
      <ServiceCompliance key={clinic} clinic={clinic} pinKey="company.spc" />
    </div>
  );
}

const ROSTER_KEY = 'people-roster';
const INACTIVE_KEY = 'people-inactive';
const profileKey = (id: number): string => `person-profile:v5:${id}`;
const driveKey = (id: number): string => `person-drive:${id}`;

export interface Person {
  id: number;
  file_no: string;
  full_name: string;
  role: string | null;
  status: string | null;
  company_code: string;
  home_location_code: string;
  location_name: string;
  hire_date: string | null;
  termination_date: string | null;
  employment_type: string | null;
  email: string | null;
  birthday_md: string | null;
  /** ITEM 17 — stored, never derived from Drive. GET /api/people carries both
   * on every roster row so the roster column needs no per-person drill-in. */
  nhp_on_file: boolean;
  nhp_url: string | null;
  note_count?: number;
}

/** rate_type is a fixed vocabulary enforced by a database CHECK (migration
 * 003): HOURLY, SERVICE, COMMISSION, SALARY, UNKNOWN. The add form below
 * offers exactly these — a free-text value the server would refuse anyway. */
export const RATE_TYPES = ['HOURLY', 'SERVICE', 'COMMISSION', 'SALARY', 'UNKNOWN'] as const;

interface Rate {
  rate_type: string;
  amount: string;
  effective_from: string;
  effective_to: string | null;
  source_file: string;
  recorded_by: string | null;
  recorded_at: string | null;
}

/** note_type is likewise a fixed vocabulary (migration 034): NOTE, WRITE_UP,
 * COACHING, TERMINATION. A new note defaults to NOTE. */
export const NOTE_TYPES = ['NOTE', 'WRITE_UP', 'COACHING', 'TERMINATION'] as const;

interface Note {
  id: number;
  note_type: string;
  body: string;
  author: string | null;
  occurred_on: string;
  created_at?: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

interface PersonDocument {
  kind: string;
  file_name: string;
  url: string | null;
  uploaded_at: string;
}

export interface ServiceHoursPayroll {
  adpWeek: number;
  startDate: string;
  hours: number;
}

export interface ServiceHoursData {
  raiseDate: string | null;
  sinceRaise: number;
  allPayrolls: number;
  gapHours: number;
  milestones?: number;
  towardCurrent?: number;
  hoursToGo?: number;
  alsoPaidAs: { locationName: string; fileNo: string }[];
  payrolls: ServiceHoursPayroll[];
}

export interface PersonProfileData {
  employee: Person & {
    first_name: string;
    last_name: string;
    termination_date: string | null;
    birthday_md: string | null;
    home_phone: string | null;
    cell_phone: string | null;
    address: string | null;
    updated_by: string | null;
    nhp_on_file: boolean;
    nhp_url: string | null;
    nhp_marked_at: string | null;
    nhp_marked_by: string | null;
    /** When the one NHP lookup for an inactive person ran (migration 092). */
    nhp_lookup_at?: string | null;
  };
  rates: Rate[];
  notes: Note[];
  documents: PersonDocument[];
  serviceHours?: ServiceHoursData | null;
}

/** One file inside an employee's Drive folder — GET /api/v1/people/:id/drive.
 * No download link, on purpose (owner ruling, 2026-08-16): `webViewLink`
 * opens the Google viewer in a new tab, and the app never uploads. */
interface DriveDocument {
  id: string;
  name: string;
  mimeType: string | null;
  webViewLink: string | null;
  modifiedTime: string | null;
}

/** A subfolder of the employee's Drive folder (e.g. "Paystubs"), or the
 * "Documents" bucket for files sitting directly in it. */
interface DriveSection {
  name: string;
  files: DriveDocument[];
  folderUrl?: string;
}

/**
 * `matched: false` is not an error — it is the feature working (INV-9): no
 * Drive folder configured for this clinic, no `Active` folder, or no folder
 * matching this employee's name, each with its own `reason` rendered
 * verbatim rather than folded into one generic "not found".
 */
export interface PersonDriveData {
  configured: boolean;
  matched: boolean;
  folderName?: string;
  folderUrl?: string;
  sections?: DriveSection[];
  reason?: string;
}

/** Everything a search box needs to match against, in memory. */
function matches(person: Person, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    person.full_name.toLowerCase().includes(q) ||
    (person.role ?? '').toLowerCase().includes(q) ||
    (person.status ?? '').toLowerCase().includes(q) ||
    person.location_name.toLowerCase().includes(q) ||
    person.file_no.toLowerCase().includes(q) ||
    (person.email ?? '').toLowerCase().includes(q)
  );
}

export function PeoplePage(): JSX.Element {
  const [clinic, setClinic] = useState<string | null>(null);
  const [extraTab, setExtraTab] = useState<string | null>(() => tabFromHash(window.location.hash || ''));
  // A Desk-card link (`/people#pay-hours-MEWP`) can name a clinic; opening
  // the tab by clicking it carries none, and the component falls back to
  // whichever clinic the roster view was already showing.
  const [payHoursClinic, setPayHoursClinic] = useState<LocationCode | null>(() => {
    const m = PAY_HOURS_HASH_RE.exec(window.location.hash || '');
    return (m?.[2] as LocationCode) ?? null;
  });
  // F-44: read from the shared resource cache (lib/store.ts), not
  // component-local state — a page unmounts on route change, so local state
  // refetched every time someone navigated back here. The cache fetches this
  // key at most once; navigating away and back reads memory (INV-1).
  const roster = useResource<{ people: Person[] }>(ROSTER_KEY, () => authedGet('/api/people'));
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const m = /^#person-(\d+)(-hours)?$/.exec(window.location.hash || '');
    return m ? Number(m[1]) : null;
  });
  const [openHours, setOpenHours] = useState(() => /#person-\d+-hours$/.test(window.location.hash || ''));
  const [notePerson, setNotePerson] = useState<Person | null>(null);
  const profile = useResource<PersonProfileData>(
    selectedId !== null ? profileKey(selectedId) : null,
    () => authedGet<PersonProfileData>(`/api/people/${selectedId}`)
  );

  // INV-1: filtering happens over the already-fetched roster in memory.
  // Typing in the search box never calls fetch again.
  const filtered = useMemo(() => {
    if (roster.status !== 'ready') return [];
    return roster.data.people.filter((p) => matches(p, query));
  }, [roster, query]);

  function openProfile(id: number, hours = false): void {
    setSelectedId(id);
    setOpenHours(hours);
    window.location.hash = hours ? `person-${id}-hours` : `person-${id}`;
  }

  function closeProfile(): void {
    setSelectedId(null);
    setOpenHours(false);
    if (/^#person-/.test(window.location.hash || '')) {
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }

  useEffect(() => {
    function applyHash(): void {
      const m = /^#person-(\d+)(-hours)?$/.exec(window.location.hash || '');
      setSelectedId(m ? Number(m[1]) : null);
      setOpenHours(Boolean(m?.[2]));
      const p = PAY_HOURS_HASH_RE.exec(window.location.hash || '');
      if (p) {
        setExtraTab(PAY_HOURS_TAB_ID);
        setPayHoursClinic((p[2] as LocationCode) ?? null);
      }
      const tab = tabFromHash(window.location.hash || '');
      if (tab === HR_TAB_ID || tab === SPC_TAB_ID) setExtraTab(tab);
    }
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  // An open profile is its own view. The roster refetch that follows a
  // Details save used to sit in front of this check: a fault (or a late
  // result from a save that had already unmounted) replaced the form with
  // the roster error and the Save button vanished.
  if (selectedId !== null) {
    return (
      <div class="main" data-testid="person-profile-view">
        <div class="page-header">
          <button type="button" class="link" data-testid="back-to-roster" onClick={closeProfile}>
            Back to People
          </button>
        </div>
        {profile.status === 'loading' && <EmptyState label="Loading profile…" />}
        {profile.status === 'fault' && <FaultState name="profile_load_failed" detail={profile.error} />}
        {profile.status === 'ready' && <PersonProfile profile={profile.data} scrollToHours={openHours} />}
      </div>
    );
  }

  if (roster.status === 'loading') {
    return (
      <div class="main">
        <div class="page-header">
          <div class="section-head-row">
            <h1>People</h1>
          </div>
        </div>
        <EmptyState label="Loading roster…" />
      </div>
    );
  }

  if (roster.status === 'fault') {
    return (
      <div class="main">
        <div class="page-header">
          <div class="section-head-row">
            <h1>People</h1>
          </div>
        </div>
        <FaultState name="roster_load_failed" detail={roster.error} />
      </div>
    );
  }

  // The live app's own shape (ground truth: the deployed board, screenshotted
  // 2026-08-15): one clinic at a time, then a card per bucket — Manager, Front
  // Desk Associates, LMTs & ESTs — and inactive people in their own card at the
  // bottom rather than mixed in. The rebuild was showing one flat roster of all
  // 115 people across all four clinics, which is not a smaller version of that
  // page; it is a different page.
  const clinics = visibleClinics();
  const activeClinic = activeClinicFrom(clinic, clinics);
  const here = filtered.filter((p) => p.home_location_code === activeClinic);
  const buckets = bucketPeople(here);

  return (
    <div class="main">
      <div class="page-header">
        <div class="section-head-row">
          <h1>People</h1>
          <div class="section-head-actions">
            <FavoriteStar itemKey="people.roster" />
          </div>
        </div>
      </div>

      {/* The search box rides on the clinic row, right-aligned (owner,
          2026-08-15). It filters in memory — INV-1: zero network calls per
          keystroke — so where it sits is purely a layout question. */}
      <ClinicTabs
        clinics={clinics}
        active={extraTab ?? activeClinic}
        onSelect={(code) => { setClinic(code); setExtraTab(null); }}
        after={[
          { id: PAY_HOURS_TAB_ID, label: 'Pay & Hours' },
          { id: HR_TAB_ID, label: 'HR' },
          { id: SPC_TAB_ID, label: 'SPC' },
          ...(canSeeManagementTab() ? [{ id: MANAGEMENT_TAB_ID, label: 'Management' }] : []),
        ]}
        onSelectAfter={(id) => {
          // A direct click (not a Desk-card hash link) carries no clinic of
          // its own — PayHours then falls back to the roster's own
          // currently-active clinic.
          setPayHoursClinic(null);
          setExtraTab(id);
        }}
        trailing={extraTab ? null : (
          <input
            type="search"
            class="people-search"
            data-testid="people-search"
            placeholder="Search name, role, status, clinic, file #"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          />
        )}
      />

      {extraTab === PAY_HOURS_TAB_ID ? (
        <PayHours initialClinic={(payHoursClinic ?? activeClinic) as LocationCode} />
      ) : extraTab === HR_TAB_ID ? (
        <CompanyHrPane />
      ) : extraTab === SPC_TAB_ID ? (
        <PeopleSpc initialClinic={activeClinic} />
      ) : extraTab === MANAGEMENT_TAB_ID && canSeeManagementTab() ? (
        <PeopleManagement />
      ) : (
        <>
          {roster.data.people.length === 0 ? (
            <EmptyState label="No roster loaded yet." />
          ) : here.length === 0 ? (
            <EmptyState label={query ? 'No one at this clinic matches that search.' : 'No one at this clinic yet.'} />
          ) : (
            DISPLAYED_BUCKETS.map((bucket) => (
              <PeopleCard
                key={bucket}
                title={BUCKET_TITLE[bucket]}
                people={buckets[bucket]}
                onOpen={openProfile}
                onOpenNote={setNotePerson}
              />
            ))
          )}
          <InactiveEmployeesPanel clinic={activeClinic} onOpenNote={setNotePerson} />
        </>
      )}
      {notePerson ? (
        <PersonNoteDrawer
          person={notePerson}
          onClose={() => setNotePerson(null)}
        />
      ) : null}
    </div>
  );
}

// The display buckets the live app groups by, in its own order. Derived from
// `role`, which is the only classification this rebuild's roster carries — the
// live app does richer work here (`pe_bucketFor_` maps a raw ADP position code
// and is documented as logic that exists nowhere but that code), so an
// unrecognised role lands in "Other" by name rather than being guessed into a
// bucket it may not belong to.
// INACTIVE stays out of the role cards (owner, 2026-08-17) so a terminated
// person never lands back on the service floor. Owner, 2026-08-19: a button
// at the bottom of each clinic opens a name + termination-date list — for
// insurance-invoice checks — and is closed until clicked.
const BUCKET_ORDER = ['MANAGER', 'FRONT_DESK', 'SERVICE', 'OTHER', 'INACTIVE'] as const;
const DISPLAYED_BUCKETS = ['MANAGER', 'FRONT_DESK', 'SERVICE', 'OTHER'] as const;
type Bucket = (typeof BUCKET_ORDER)[number];

const BUCKET_TITLE: Record<Bucket, string> = {
  ...ROLE_BUCKET_TITLE,
  INACTIVE: 'Inactive employees',
};

// The grey one-line count under each card title is GONE (owner, 2026-08-17:
// "11 active on the service floor. / 6 whose position does not map to a known
// bucket. / 0 no longer active at this clinic. … remove these"). The table
// under the title is the answer; a sentence counting its own rows is noise.

/**
 * The role codes that mean "front desk".
 *
 * BUG, owner 2026-08-15: "what Front Desk Associates are those — those position
 * says FD so they should be in FD." The bucket tested for the string
 * `FRONT_DESK`; the roster actually stores `FD`. So every front-desk associate
 * fell through to "Other roles" while the Front Desk card said "0 active at the
 * front desk" — on a page whose whole job is showing who works where.
 *
 * Both spellings are accepted rather than one being picked, because `FD` is
 * what the imported ADP data says and `FRONT_DESK` is what this app's own role
 * vocabulary says (SPEC-001 §6 names FRONT_DESK as a role). They are the same
 * job; a person should land in the same card whichever wrote the row.
 *
 * `FD_MANAGER` is in the schema's own role CHECK (migration 001) and is here
 * too: they work the front desk. The MANAGER card says "the location manager",
 * singular, and a front-desk lead is not that — putting them there would make
 * that card lie. Spec-silent judgment call; flag it if the owner disagrees.
 */
// FRONT_DESK_ROLES and SERVICE_ROLES live in lib/roleBuckets.ts, shared with
// Pay & Hours (owner, 2026-09-23) so both screens group people identically.

/**
 * "FULL_TIME" is a database enum, not a word anyone says (owner, 2026-08-15:
 * "FT/PT should be FT or PT, not Full_time"). Unrecognised values are returned
 * as they are rather than blanked — an unexpected code is worth seeing, and
 * silently hiding it is how a data problem stays invisible.
 */
export function employmentShort(value: string | null): string {
  switch ((value ?? '').toUpperCase()) {
    case 'FULL_TIME': return 'FT';
    case 'PART_TIME': return 'PT';
    case '': return EM_DASH;
    default: return value as string;
  }
}

export function bucketPeople(people: Person[]): Record<Bucket, Person[]> {
  const out: Record<Bucket, Person[]> = {
    MANAGER: [], FRONT_DESK: [], SERVICE: [], OTHER: [], INACTIVE: [],
  };
  for (const p of people) {
    const status = (p.status ?? '').toUpperCase();
    if (status && status !== 'ACTIVE') { out.INACTIVE.push(p); continue; }
    out[roleBucket(p.role)].push(p);
  }
  for (const list of Object.values(out)) list.sort((a, b) => compareByLastName(a.full_name, b.full_name));
  return out;
}

/** Closed until clicked — then one fetch of terminated names, not the sign-in
 * roster (owner, 2026-08-19). */
function InactiveEmployeesPanel(
  { clinic, onOpenNote }:
  { clinic: string; onOpenNote: (person: Person) => void },
): JSX.Element {
  const [open, setOpen] = useState(false);
  const load = useResource<{ people: Person[] }>(
    open ? INACTIVE_KEY : null,
    () => authedGet('/api/people/inactive'),
  );
  const people = load.status === 'ready'
    ? load.data.people.filter((p) => p.home_location_code === clinic)
      .slice()
      .sort((a, b) => compareByLastName(a.full_name, b.full_name))
    : [];
  return (
    <div class="inactive-employees" data-testid="inactive-employees">
      <button
        type="button"
        class="btn"
        data-testid="inactive-employees-toggle"
        onClick={() => setOpen((v) => !v)}
      >
        Inactive Employees
      </button>
      {open ? (
        <section class="card" data-testid="inactive-employees-table">
          {load.status === 'loading' ? (
            <p class="empty">Loading inactive employees…</p>
          ) : load.status === 'fault' ? (
            <FaultState name="inactive_load_failed" detail={load.error} />
          ) : people.length === 0 ? (
            <p class="empty">Nobody inactive at this clinic.</p>
          ) : (
            <div class="table-wrap">
              <table class="roster-table">
                <colgroup>
                  <col style="width: 44%" />
                  <col style="width: 22%" />
                  <col style="width: 22%" />
                  <col style="width: 12%" />
                </colgroup>
                <thead>
                  <tr>
                    <th class="name">Name</th>
                    <th>Hire date</th>
                    <th>Term date</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => (
                    <tr key={p.id} data-testid={`inactive-row-${p.id}`}>
                      <td class="name">{p.full_name}</td>
                      <td>{p.hire_date ? formatDate(p.hire_date) : EM_DASH}</td>
                      <td>{p.termination_date ? formatDate(p.termination_date) : EM_DASH}</td>
                      <td>
                        <NoteMark
                          hasNote={(p.note_count ?? 0) > 0}
                          label={p.full_name}
                          testId={`person-note-${p.id}`}
                          onOpen={() => onOpenNote(p)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

/** One bucket, as the live app draws it: a card, a bold title, a one-line grey
 * count under it, then the table. An empty bucket says so by name instead of
 * disappearing — a clinic with no manager is a fact worth seeing (INV-9). */
function PeopleCard(
  { title, people, onOpen, onOpenNote }:
  {
    title: string;
    people: Person[];
    onOpen: (id: number) => void;
    onOpenNote: (person: Person) => void;
  },
): JSX.Element {
  // N-084 (Request Ledger, owner 2026-09-08): a register-created employee
  // (db/load_week_package.js) lands ACTIVE with file #, name and role and
  // nothing else — no hire date, until someone fills the profile in. Owner
  // and director (Candace added 2026-10-06) — the same gate as the server's
  // needs-details read and the New Hires desk panel.
  const showHireDetailsBadge = canSeeNewHires(session.value.me?.role);
  // Click-to-sort (owner, 2026-08-19). NHP sorts on whether the form is on
  // file, not on the ✓ glyph, so one click groups everyone still missing one —
  // which is the question that column exists to answer.
  const sorted = useTableSort(people, {
    name: (p) => lastNameKey(p.full_name),
    employment: (p) => p.employment_type,
    position: (p) => p.role,
    birthday: (p) => p.birthday_md,
    nhp: (p) => Boolean(p.nhp_on_file),
    hired: (p) => p.hire_date,
  });
  const wrapRef = useRef<HTMLDivElement>(null);
  useSparkleOnView(wrapRef, sorted.rows.map((p) => p.id).join(','));
  return (
    <section class="card">
      <div class="section-head">
        <h2>{title}</h2>
      </div>
      {people.length === 0 ? (
        <p class="empty">Nobody in this group at this clinic.</p>
      ) : (
        <div class="table-wrap" ref={wrapRef}>
          {/* FIXED COLUMN WIDTHS, shared by every card on the page (owner,
              2026-08-17: "the tables MUST be lined up the same in columns…
              FD and lower table LMT and EST columns dont line up"). Each card
              is its own <table>, so by default each one sized its columns to
              its OWN longest cell and no two lined up. `table-layout: fixed`
              plus this colgroup makes every card measure identically. */}
          <table class="roster-table">
            <colgroup>
              <col style="width: 28%" />
              <col style="width: 8%" />
              <col style="width: 14%" />
              <col style="width: 16%" />
              <col style="width: 10%" />
              <col style="width: 8%" />
              <col style="width: 16%" />
            </colgroup>
            <thead>
              <tr>
                <SortableTh label="Name" sortKey="name" state={sorted.state} onSort={sorted.toggle} className="name" />
                <SortableTh label="FT/PT" sortKey="employment" state={sorted.state} onSort={sorted.toggle} />
                {/* No Email column here (owner, 2026-08-16: "remove employee
                    email from batch table view .. its in profile as it should
                    be"). It is on the employee's own Details card, which is
                    where a contact detail belongs; the roster is a list, and a
                    column of addresses made it wide and hard to scan. Search
                    still matches on email — see matches() — so typing someone's
                    address still finds them. */}
                <SortableTh label="Position" sortKey="position" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Hire date" sortKey="hired" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="Birthday" sortKey="birthday" state={sorted.state} onSort={sorted.toggle} />
                <SortableTh label="NHP" sortKey="nhp" state={sorted.state} onSort={sorted.toggle} />
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {sorted.rows.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`person-row-${p.id}`}
                  data-birthday-soon={birthdaySoon(p.birthday_md) ? 'true' : undefined}
                  data-sparkle={birthdaySoon(p.birthday_md) ? '' : undefined}
                  onClick={() => onOpen(p.id)}
                >
                  <td class="name">
                    <span onClick={(ev) => ev.stopPropagation()}>
                      <FavoriteStar itemKey={`person.${p.id}`} />
                    </span>
                    {' '}
                    <button type="button" class="link">{p.full_name}</button>
                  </td>
                  <td>{employmentShort(p.employment_type)}</td>
                  <td>{p.role ?? EM_DASH}</td>
                  <td>
                    {p.hire_date ? formatDate(p.hire_date) : EM_DASH}
                    {!p.hire_date && showHireDetailsBadge ? (
                      <span
                        class="badge warn"
                        data-testid={`needs-hire-details-${p.id}`}
                        title="No hire date on file — open the profile to fill it in."
                      >
                        {' '}Needs hire details
                      </span>
                    ) : null}
                  </td>
                  <td class={birthdaySoon(p.birthday_md) ? 'birthday-date' : undefined}>
                    {p.birthday_md?.trim() ? p.birthday_md : EM_DASH}
                  </td>
                  <td>
                    <NhpMark id={p.id} onFile={p.nhp_on_file} url={p.nhp_url} />
                  </td>
                  <td>
                    <NoteMark
                      hasNote={(p.note_count ?? 0) > 0}
                      label={p.full_name}
                      testId={`person-note-${p.id}`}
                      onOpen={() => onOpenNote(p)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// The live app also shows PHONE, NHP, DOB, PAY RATE and per-row Edit / Set
// inactive controls. As of 2026-08-16 phone, address, DOB and hire date exist
// and are editable ON THE PROFILE (migration 032, PATCH /api/v1/people/:id) —
// not per row in the roster, which is a list, not a form. NHP detection and
// "set inactive" still do not exist and are absent rather than drawn as dead
// controls; a termination is not a profile edit. Pay rate is a manager-visible
// field under D-32 and lives on the profile view, one click in.
const EM_DASH = '\u2014';

/** Employee role codes (migration 002) — editable on the profile, owner 2026-08-19. */
export const EMPLOYEE_ROLES = [
  'FD', 'FD_MANAGER', 'EST', 'LMT', 'STRETCH', 'MANAGER', 'SALES', 'OTHER', 'UNKNOWN',
] as const;

/** The roster/profile mark for "NHP on file" \u2014 plain text, not a dingbat or
 * emoji glyph (INV-8 forbids those in built output; a checkmark character
 * failed that gate the first time this was written). */
const NHP_MARK = 'On file';

/** Roster NHP: the same Drive mark Tasks uses for an attachment. Clickable
 * when a URL is stored; still shown (not a dash) when the form is on file
 * but nobody has pasted the link yet. Never looks Drive up (item 17). */
function NhpMark({ id, onFile, url }: { id: number; onFile: boolean; url: string | null }): JSX.Element {
  if (!onFile) return <>{EM_DASH}</>;
  if (!url) {
    return (
      <span
        class="drive-mark"
        role="img"
        aria-label="New hire paperwork on file"
        title="New hire paperwork on file"
        data-testid={`nhp-mark-${id}`}
      />
    );
  }
  return (
    <a
      class="drive-mark"
      href={withGoogleAccount(url, session.value.me?.email ?? null)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`nhp-link-${id}`}
      aria-label="Open new hire paperwork"
      title="Open new hire paperwork"
      onClick={(ev) => ev.stopPropagation()}
    />
  );
}

/** The fields PATCH /api/v1/people/:id will accept, and the only ones this
 * form may send. It mirrors EMPLOYEE_PROFILE_FIELDS in `src/people.js` — the
 * server drops anything else, so a key added here alone would silently do
 * nothing. There is deliberately no rate, SSN or bank field: pay rates are
 * read-only to a manager (R-5) and the secure fields are not on this table at
 * all (SPEC-001 §7). */
const DETAIL_FIELDS = [
  { key: 'employment_type', label: 'FT / PT', kind: 'ftpt' as const },
  { key: 'role', label: 'Position', kind: 'role' as const },
  { key: 'cell_phone', label: 'Cell phone' },
  { key: 'home_phone', label: 'Home phone' },
  { key: 'address', label: 'Address' },
  { key: 'email', label: 'Email' },
  { key: 'birthday_md', label: 'Birthday', hint: 'MM/DD' },
  { key: 'hire_date', label: 'Hire date', kind: 'date' as const },
] as const;

type DetailKey = (typeof DETAIL_FIELDS)[number]['key'];

/**
 * The value to compare against and to prefill with, for ONE field.
 *
 * `GET /api/people/:id` returns `hire_date` as a full timestamp
 * (`2023-03-24T00:00:00.000Z`) while `GET /api/v1/company` returns its dates as
 * plain `2008-03-06`. Both are real, both are live, so this handles both rather
 * than betting on one.
 *
 * `<input type="date">` silently renders EMPTY for anything that is not
 * `YYYY-MM-DD`, so on 2026-08-16 the hire date box came up blank in the browser
 * for an employee who plainly has one — Donna Jovel, hired 03/24/2023. The read
 * view was right and the editor was not.
 *
 * Saving other fields still worked (the form STATE held the timestamp even
 * while the box looked empty, so it compared equal and was skipped), but a
 * blank box next to a filled-in read view is an invitation to type over it, and
 * the first person to do that would have been correcting something that was
 * never wrong.
 */
function detailValue(e: PersonProfileData['employee'], key: DetailKey): string {
  const raw = (e as unknown as Record<string, string | null>)[key] ?? '';
  if (key === 'hire_date' && /^\d{4}-\d{2}-\d{2}T/.test(raw)) return raw.slice(0, 10);
  if (key === 'employment_type') {
    const u = raw.trim().toUpperCase();
    if (u === 'FT' || u === 'FULL_TIME') return 'FULL_TIME';
    if (u === 'PT' || u === 'PART_TIME') return 'PART_TIME';
    return '';
  }
  if (key === 'role') return raw.trim().toUpperCase() || 'UNKNOWN';
  return raw;
}

/** Prefill from the RAW value, never the displayed one — an em dash in a text
 * box is the bug the Company page paid for first (D-38). */
function rawDetailValues(e: PersonProfileData['employee']): Record<DetailKey, string> {
  const out = {} as Record<DetailKey, string>;
  for (const field of DETAIL_FIELDS) {
    out[field.key] = detailValue(e, field.key);
  }
  return out;
}

function PersonDetails({ profile }: { profile: PersonProfileData }): JSX.Element {
  const e = profile.employee;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<DetailKey, string>>(() => rawDetailValues(e));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  function startEdit(): void {
    setForm(rawDetailValues(e));
    setError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setForm(rawDetailValues(e));
    setError(null);
    setEditing(false);
  }

  async function save(): Promise<void> {
    const patch: Record<string, string> = {};
    const cleared: string[] = [];
    for (const field of DETAIL_FIELDS) {
      // The SAME normalisation the prefill used. Comparing the box against the
      // un-normalised value is what made every save look like a cleared field.
      const raw = detailValue(e, field.key);
      if (form[field.key] === raw) continue;
      // EMPTYING A FIELD IS NOT A SAVE. The server treats absent and empty the
      // same, so a blank would come back 200 having changed nothing — a save
      // that reports success and does nothing is the worst outcome available.
      if (form[field.key].trim() === '' && raw !== '') { cleared.push(field.key); continue; }
      patch[field.key] = form[field.key];
    }
    if (cleared.length > 0) {
      setError('A field cannot be emptied yet — put the value back, or press Cancel.');
      return;
    }
    if (Object.keys(patch).length === 0) { setEditing(false); return; }

    setBusy(true);
    setError(null);
    try {
      await authedWrite('PATCH', `/api/v1/people/${e.id}`, patch);
      if (!alive.current) return;
      await refetchResource<PersonProfileData>(
        profileKey(e.id),
        () => authedGet<PersonProfileData>(`/api/people/${e.id}`)
      );
      if (!alive.current) return;
      await refetchResource(ROSTER_KEY, () => authedGet('/api/people'));
      if (!alive.current) return;
      setEditing(false);
    } catch (err) {
      // The server's own refusal text ("birthday must be MM/DD…"), never a
      // generic "Something went wrong" (INV-9).
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      if (alive.current) setBusy(false);
    }
  }

  return (
    <section data-testid="person-details">
      {/* Edit details sits at the right end of the heading's own line
          (owner, 2026-08-16: "edit profile, edit task, delete etc ...
          should be ... same line as table heading") — `.section-head-row`
          is the one shared rule for that, defined once in global.css. */}
      <div class="section-head">
        <div class="section-head-row">
          <h3>Details</h3>
          <div class="section-head-actions">
            {editing ? (
              <button type="button" class="btn" onClick={cancelEdit} disabled={busy}>Cancel</button>
            ) : (
              <button type="button" class="btn" onClick={startEdit}>Edit details</button>
            )}
            {editing && (
              <button type="button" class="btn primary" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
      {error ? <small class="bad" role="alert">{error}</small> : null}

      <table>
        <tbody>
          {DETAIL_FIELDS.map((field) => {
            const raw = detailValue(e, field.key);
            return (
              <tr key={field.key}>
                <th scope="row">{field.label}</th>
                <td>
                  {editing ? (
                    'kind' in field && field.kind === 'ftpt' ? (
                      <select
                        aria-label={field.label}
                        value={form[field.key]}
                        onChange={(ev) => setForm({
                          ...form,
                          [field.key]: (ev.target as HTMLSelectElement).value,
                        })}
                      >
                        <option value="">{EM_DASH}</option>
                        <option value="FULL_TIME">FT</option>
                        <option value="PART_TIME">PT</option>
                      </select>
                    ) : 'kind' in field && field.kind === 'role' ? (
                      <select
                        aria-label={field.label}
                        value={form[field.key]}
                        onChange={(ev) => setForm({
                          ...form,
                          [field.key]: (ev.target as HTMLSelectElement).value,
                        })}
                      >
                        {EMPLOYEE_ROLES.map((code) => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={'kind' in field && field.kind === 'date' ? 'date' : 'text'}
                        aria-label={field.label}
                        placeholder={'hint' in field ? field.hint : undefined}
                        value={form[field.key]}
                        onInput={(ev) => setForm({
                          ...form,
                          [field.key]: (ev.target as HTMLInputElement).value,
                        })}
                      />
                    )
                  ) : (
                    raw === ''
                      ? EM_DASH
                      : (field.key === 'hire_date' ? formatDate(raw)
                        : field.key === 'employment_type' ? employmentShort(raw)
                        : raw)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/**
 * ITEM 17 — NHP is a stored fact (nhp_on_file, nhp_url), never a Drive
 * lookup. A checkbox plus a link field, saved through the same PATCH
 * PersonDetails uses, and — when the server has recorded who last touched it
 * — a "marked by X on DATE" line under the read view.
 */
function PersonNHP({ profile }: { profile: PersonProfileData }): JSX.Element {
  const e = profile.employee;
  const [editing, setEditing] = useState(false);
  const [onFile, setOnFile] = useState(e.nhp_on_file);
  const [url, setUrl] = useState(e.nhp_url ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit(): void {
    setOnFile(e.nhp_on_file);
    setUrl(e.nhp_url ?? '');
    setError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setOnFile(e.nhp_on_file);
    setUrl(e.nhp_url ?? '');
    setError(null);
    setEditing(false);
  }

  async function save(): Promise<void> {
    const patch: Record<string, boolean | string> = {};
    if (onFile !== e.nhp_on_file) patch.nhp_on_file = onFile;
    if (url !== (e.nhp_url ?? '')) patch.nhp_url = url;
    if (Object.keys(patch).length === 0) { setEditing(false); return; }

    setBusy(true);
    setError(null);
    try {
      await authedWrite('PATCH', `/api/v1/people/${e.id}`, patch);
      await refetchResource<PersonProfileData>(
        profileKey(e.id),
        () => authedGet<PersonProfileData>(`/api/people/${e.id}`)
      );
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function addFile(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadPersonNhp(e.id, file);
      await refetchResource<PersonProfileData>(
        profileKey(e.id),
        () => authedGet<PersonProfileData>(`/api/people/${e.id}`)
      );
      await refetchResource(ROSTER_KEY, () => authedGet('/api/people'));
      await refetchResource(driveKey(e.id), () => authedGet(`/api/v1/people/${e.id}/drive`));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      input.value = '';
    }
  }

  return (
    <section data-testid="person-nhp">
      <div class="section-head">
        <div class="section-head-row">
          <h3>NHP</h3>
          <div class="section-head-actions">
            {editing ? (
              <button type="button" class="btn" onClick={cancelEdit} disabled={busy}>Cancel</button>
            ) : (
              <>
                {!e.nhp_on_file ? (
                  <label class="btn" data-testid="nhp-add-label">
                    {busy ? 'Adding…' : 'Add'}
                    <input
                      type="file"
                      class="visually-hidden"
                      aria-label="Add NHP file"
                      data-testid="nhp-add"
                      disabled={busy}
                      onChange={(ev) => void addFile(ev)}
                    />
                  </label>
                ) : null}
                <button type="button" class="btn" onClick={startEdit} disabled={busy}>Edit NHP</button>
              </>
            )}
            {editing && (
              <button type="button" class="btn primary" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
      {error ? <small class="bad" role="alert">{error}</small> : null}

      {editing ? (
        <div class="nhp-editor">
          <label>
            <input
              type="checkbox"
              aria-label="NHP on file"
              checked={onFile}
              onChange={(ev) => setOnFile((ev.target as HTMLInputElement).checked)}
            />
            {' '}On file
          </label>
          <input
            type="text"
            aria-label="NHP link"
            placeholder="Link (optional)"
            value={url}
            onInput={(ev) => setUrl((ev.target as HTMLInputElement).value)}
          />
        </div>
      ) : (
        <div>
          {e.nhp_on_file ? (
            e.nhp_url ? (
              <a class="doc-link" href={e.nhp_url} target="_blank" rel="noopener noreferrer" data-testid="nhp-profile-link">
                {NHP_MARK}
              </a>
            ) : NHP_MARK
          ) : EM_DASH}
          {e.nhp_marked_by && e.nhp_marked_at ? (
            <div class="empty">marked by {e.nhp_marked_by} on {formatDate(e.nhp_marked_at)}</div>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** Pay rates are read-only here. New rates come from Payroll entry only. */
function RatesSection({ profile }: { profile: PersonProfileData }): JSX.Element {
  const sorted = [...profile.rates].sort(
    (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
  );

  return (
    <section data-testid="person-rates">
      <h3>Rates</h3>
      {sorted.length === 0 ? (
        <EmptyState label="No rate history." />
      ) : (
        <ul>
          {sorted.map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>
              {Number(r.amount).toFixed(2)} — from {formatDate(r.effective_from)}
              {r.effective_to ? ` to ${formatDate(r.effective_to)}` : ''}
              {r.recorded_by ? ` · recorded by ${r.recorded_by}` : ''}
              {r.recorded_at ? ` on ${formatDate(r.recorded_at)}` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

async function refreshPersonNotes(personId: number): Promise<void> {
  await refetchResource<PersonProfileData>(
    profileKey(personId),
    () => authedGet<PersonProfileData>(`/api/people/${personId}`),
  );
  await refetchResource<{ people: Person[] }>(ROSTER_KEY, () => authedGet('/api/people'));
  await refetchResource<{ people: Person[] }>(INACTIVE_KEY, () => authedGet('/api/people/inactive'));
  const n = (peekResource<PersonProfileData>(profileKey(personId))?.notes ?? []).length;
  for (const key of [ROSTER_KEY, INACTIVE_KEY]) {
    const pack = peekResource<{ people: Person[] }>(key);
    if (!pack) continue;
    await persistResource(key, {
      ...pack,
      people: pack.people.map((p) => (p.id === personId ? { ...p, note_count: n } : p)),
    });
  }
}

function PersonNoteDrawer(
  { person, onClose }:
  { person: Person; onClose: () => void },
): JSX.Element {
  const profile = useResource<PersonProfileData>(
    profileKey(person.id),
    () => authedGet<PersonProfileData>(`/api/people/${person.id}`),
  );
  const notes = profile.status === 'ready'
    ? (profile.data.notes ?? []).map((n) => ({
      id: n.id,
      body: n.body,
      author: n.author,
      at: n.created_at || n.occurred_on,
    }))
    : [];

  return (
    <NotePanel
      title={person.full_name}
      notes={notes}
      loading={profile.status === 'loading'}
      onClose={onClose}
      onSave={async (text) => {
        await authedWrite('POST', `/api/v1/people/${person.id}/notes`, { body: text });
        await refreshPersonNotes(person.id);
      }}
      onDelete={async (id) => {
        await authedWrite('DELETE', `/api/v1/notes/${id}`);
        await refreshPersonNotes(person.id);
      }}
    />
  );
}

/**
 * ITEM 18 — one note at a time, in a BIG textarea (owner's complaint was that
 * the box "collapses" — `rows` plus a CSS `min-height` on `.notes-entry` keep
 * it from shrinking as he types). Add / edit / delete, newest note first.
 * Deletion is server-enforced per clinic for a manager (R-... not renumbered
 * here) — this UI does not gate who sees the controls, only the write does.
 */
function NotesSection({ profile }: { profile: PersonProfileData }): JSX.Element {
  const e = profile.employee;
  const [newBody, setNewBody] = useState('');
  const [newType, setNewType] = useState<string>('NOTE');
  const { busy, error, run } = useWrite();

  const sorted = [...profile.notes].sort((a, b) => b.id - a.id);

  async function addNote(): Promise<void> {
    if (!newBody.trim()) return;
    await run(async () => {
      await authedWrite('POST', `/api/v1/people/${e.id}/notes`, { body: newBody, note_type: newType });
      await refetchResource<PersonProfileData>(
        profileKey(e.id),
        () => authedGet<PersonProfileData>(`/api/people/${e.id}`)
      );
    }, () => { setNewBody(''); setNewType('NOTE'); });
  }

  return (
    <section data-testid="person-notes">
      <h3>Notes</h3>

      <form
        class="notes-add"
        onSubmit={(ev: Event) => { ev.preventDefault(); void addNote(); }}
      >
        <textarea
          class="notes-entry"
          aria-label="New note"
          rows={8}
          placeholder="Add a note…"
          value={newBody}
          onInput={(ev: Event) => setNewBody((ev.target as HTMLTextAreaElement).value)}
        />
        <div class="notes-add-row">
          <select
            value={newType} aria-label="Note type"
            onChange={(ev: Event) => setNewType((ev.target as HTMLSelectElement).value)}
          >
            {NOTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button type="submit" class="btn" disabled={busy || !newBody.trim()}>
            {busy ? 'Saving…' : 'Add note'}
          </button>
        </div>
        {error ? <small class="bad" role="alert">{error}</small> : null}
      </form>

      {sorted.length === 0 ? (
        <EmptyState label="No notes." />
      ) : (
        <ul>
          {sorted.map((n) => <NoteRow key={n.id} note={n} personId={e.id} />)}
        </ul>
      )}
    </section>
  );
}

/** One note, with its own edit-in-place and inline delete confirm. NEVER
 * `window.confirm` (docs/known-traps.md: it freezes the tab to automation) —
 * a "Delete? Yes / Cancel" that replaces the row's own controls instead. */
function NoteRow({ note, personId }: { note: Note; personId: number }): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const [confirming, setConfirming] = useState(false);
  const edit = useWrite();
  const del = useWrite();

  async function saveEdit(): Promise<void> {
    await edit.run(async () => {
      await authedWrite('PATCH', `/api/v1/notes/${note.id}`, { body });
      await refetchResource<PersonProfileData>(
        profileKey(personId),
        () => authedGet<PersonProfileData>(`/api/people/${personId}`)
      );
    }, () => setEditing(false));
  }

  async function confirmDelete(): Promise<void> {
    await del.run(async () => {
      await authedWrite('DELETE', `/api/v1/notes/${note.id}`);
      await refetchResource<PersonProfileData>(
        profileKey(personId),
        () => authedGet<PersonProfileData>(`/api/people/${personId}`)
      );
    });
  }

  // The date/author/type line is this row's own "heading" — Edit and Delete
  // now sit at its right end (owner, 2026-08-16: same-line-as-heading rule),
  // using the same shared `.section-head-row` / `.section-head-actions`
  // classes the section-level headings use, not a one-off flex rule here.
  const metaLine = (
    <div class="empty">
      {formatNoteStamp(note.author, note.created_at || note.occurred_on)} — {note.note_type}
    </div>
  );

  return (
    <li data-testid={`note-${note.id}`}>
      {editing ? (
        <>
          {metaLine}
          <textarea
            class="notes-entry"
            aria-label={`Edit note ${note.id}`}
            rows={8}
            value={body}
            onInput={(ev: Event) => setBody((ev.target as HTMLTextAreaElement).value)}
          />
          <button type="button" class="btn primary" onClick={() => void saveEdit()} disabled={edit.busy}>
            {edit.busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button" class="btn"
            onClick={() => { setBody(note.body); setEditing(false); }}
            disabled={edit.busy}
          >
            Cancel
          </button>
          {edit.error ? <small class="bad" role="alert">{edit.error}</small> : null}
        </>
      ) : (
        <>
          <div class="section-head-row">
            {metaLine}
            <div class="section-head-actions">
              <button type="button" class="btn" onClick={() => setEditing(true)}>Edit</button>
              {confirming ? (
                <span data-testid={`note-${note.id}-confirm`}>
                  Delete?{' '}
                  <button type="button" class="btn" onClick={() => void confirmDelete()} disabled={del.busy}>
                    {del.busy ? 'Deleting…' : 'Yes'}
                  </button>{' '}
                  <button type="button" class="btn" onClick={() => setConfirming(false)} disabled={del.busy}>
                    Cancel
                  </button>
                </span>
              ) : (
                <button type="button" class="btn" onClick={() => setConfirming(true)}>Delete</button>
              )}
            </div>
          </div>
          <p>{note.body}</p>
          {del.error ? <small class="bad" role="alert">{del.error}</small> : null}
        </>
      )}
    </li>
  );
}

function ServiceHoursSection({ hours }: { hours: ServiceHoursData | null | undefined }): JSX.Element | null {
  if (!hours) return null;
  const also = hours.alsoPaidAs.map((r) => `${r.locationName} #${r.fileNo}`).join('; ');
  return (
    <section class="card" data-testid="person-service-hours">
      <div class="section-head"><h3>Service hours</h3></div>
      <p>
        {hours.raiseDate
          ? <>Since last raise ({formatDate(hours.raiseDate)}): </>
          : <>Since hire: </>}
        <strong>{hours.sinceRaise}</strong>
      </p>
      <p>Every payroll on file: <strong>{hours.allPayrolls}</strong></p>
      {hours.towardCurrent != null ? (
        <p data-testid="service-hours-cycle">
          Toward the next 1,000: <strong>{hours.towardCurrent}</strong>
          {hours.milestones ? ` · ${hours.milestones} milestone${hours.milestones === 1 ? '' : 's'} reached` : ''}
          {hours.hoursToGo != null ? ` · ${hours.hoursToGo} to go` : ''}
        </p>
      ) : null}
      {also ? <p>Also paid as {also}</p> : null}
      {hours.gapHours > 0 ? (
        <p class="hint">Hours before week 51 (from ADP, not a register): {hours.gapHours}</p>
      ) : null}
      {hours.payrolls.length === 0 ? (
        <EmptyState label="No payroll hours on file." />
      ) : (
        <table class="data" data-testid="person-service-hours-table">
          <thead>
            <tr>
              <th>Week</th>
              <th>Period start</th>
              <th>Hours</th>
            </tr>
          </thead>
          <tbody>
            {hours.payrolls.map((p) => (
              <tr key={p.adpWeek} data-testid={`service-hours-week-${p.adpWeek}`}>
                <td>W{p.adpWeek}</td>
                <td>{p.startDate ? formatDate(p.startDate) : '—'}</td>
                <td class="num">{p.hours || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PersonProfile({ profile, scrollToHours = false }: { profile: PersonProfileData; scrollToHours?: boolean }): JSX.Element {
  const e = profile.employee;
  // An inactive person's NHP is found ONCE, the first time anyone opens their
  // profile (owner, 2026-09-28: "loaded one time … only if someone clicks on
  // this person's link should it generate … beyond that it's just a link
  // that stays static"). The server stamps the lookup, found or not, so it
  // never runs again for this person; a link found is then static.
  useEffect(() => {
    if (e.status !== 'TERMINATED' || e.nhp_url || e.nhp_lookup_at) return;
    let cancelled = false;
    void authedWrite<{ looked: boolean; nhpUrl: string | null }>('POST', `/api/v1/people/${e.id}/nhp-lookup`, {})
      .then((r) => {
        if (cancelled || !r.looked) return;
        void refetchResource<PersonProfileData>(profileKey(e.id), () => authedGet<PersonProfileData>(`/api/people/${e.id}`));
      })
      .catch(() => { /* a failed lookup is not stamped; the next open tries again */ });
    return () => { cancelled = true; };
  }, [e.id, e.status, e.nhp_url, e.nhp_lookup_at]);
  useEffect(() => {
    if (!scrollToHours) return;
    document.querySelector('[data-testid="person-service-hours"]')?.scrollIntoView({ block: 'start' });
  }, [scrollToHours, e.id]);
  return (
    <div class="person-profile" data-testid="person-profile">
      <div class="section-head-row">
        <h2>{e.full_name}</h2>
        <FavoriteStar itemKey={`person.${e.id}`} />
      </div>
      <div>
        #{e.file_no} · {e.location_name} · {e.role ?? ''} · {e.status ?? ''}
      </div>
      {e.hire_date && <div>Hired {formatDate(e.hire_date)}</div>}

      <PersonDetails profile={profile} />
      <PersonNHP profile={profile} />
      <RatesSection profile={profile} />
      <ServiceHoursSection hours={profile.serviceHours} />
      <NotesSection profile={profile} />

      {/* The database-backed document list, which has never had a row in it:
          `employee_document` was built for an upload path this app no longer
          intends to have (owner, 2026-08-16: "the app is a reference for our
          company documents and NOTHING else"). It renders only if something
          did put a row there, so the profile shows ONE Documents heading —
          Drive's — instead of an empty card above a full one. */}
      {profile.documents.length > 0 && (
        <section>
          <h3>Documents</h3>
          <ul>
            {profile.documents.map((d, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{d.url ? <a class="doc-link" href={d.url} target="_blank" rel="noreferrer">{d.file_name}</a> : d.file_name}</li>
            ))}
          </ul>
        </section>
      )}

      <PersonDriveDocuments personId={e.id} />
    </div>
  );
}

/**
 * The narrow pilot (owner, 2026-08-16): this employee's own Drive folder,
 * fetched when the profile opens (a drill-in, same discipline as the profile
 * fetch itself — not a navigation, so INV-1 does not apply). One card per
 * section, People's own card/table idiom (R-16) — no new CSS invented here.
 * Read-only: a file name is a link that opens the Google viewer in a new
 * tab; there is no download link and no upload path (owner ruling tonight).
 */
function PersonDriveDocuments({ personId }: { personId: number }): JSX.Element {
  const drive = useResource<PersonDriveData>(
    driveKey(personId),
    () => authedGet<PersonDriveData>(`/api/v1/people/${personId}/drive`)
  );

  if (drive.status === 'loading') {
    return (
      <section class="card" data-testid="person-drive-documents">
        <div class="section-head"><h2>Drive documents</h2></div>
        <EmptyState label="Loading Drive documents…" />
      </section>
    );
  }

  if (drive.status === 'fault') {
    return (
      <section class="card" data-testid="person-drive-documents">
        <div class="section-head"><h2>Drive documents</h2></div>
        <FaultState name="drive_documents_load_failed" detail={drive.error} />
      </section>
    );
  }

  // matched: false is not a fault — it is the server's own named refusal
  // (no Drive folder registered, no Active folder, no name match). Shown
  // verbatim (INV-9): a manager seeing this text IS the feature working.
  if (!drive.data.matched) {
    return (
      <section class="card" data-testid="person-drive-documents">
        <div class="section-head"><h2>Drive documents</h2></div>
        <p class="empty">{drive.data.reason}</p>
      </section>
    );
  }

  const sections = drive.data.sections ?? [];
  if (sections.length === 0) {
    return (
      <section class="card" data-testid="person-drive-documents">
        <div class="section-head">
          <div class="section-head-row">
            <h2>Drive documents</h2>
            <div class="section-head-actions">
              <OpenFolderLink url={drive.data.folderUrl} label="Drive documents" />
            </div>
          </div>
        </div>
        <EmptyState label="No documents in this employee's Drive folder." />
      </section>
    );
  }

  return (
    <>
      {sections.map((s) => (
        <DriveSectionCard key={s.name} section={s} />
      ))}
    </>
  );
}

/** One Drive section's files, sortable by name or by when Drive last saw them
 * change (owner, 2026-08-19). Split into its own component because a hook
 * cannot live inside a `.map()` callback. */
function DriveSectionCard({ section: s }: { section: DriveSection }): JSX.Element {
  const sorted = useTableSort(s.files, {
    file: (f) => f.name,
    modified: (f) => f.modifiedTime,
  });
  return (
        <section class="card" data-testid={`person-drive-section-${s.name}`}>
          <div class="section-head">
            <div class="section-head-row">
              <h2>{s.name}</h2>
              <div class="section-head-actions">
                <OpenFolderLink url={s.folderUrl} label={s.name} />
              </div>
            </div>
          </div>
          {s.files.length === 0 ? (
            <p class="empty">No documents in this folder.</p>
          ) : (
          <div class="table-wrap">
            <table class="person-drive-files">
              <thead>
                <tr>
                  <SortableTh label="File" sortKey="file" state={sorted.state} onSort={sorted.toggle} />
                  <SortableTh label="Modified" sortKey="modified" state={sorted.state} onSort={sorted.toggle} />
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((f) => (
                  <tr key={f.id} data-testid={`person-drive-file-${f.id}`}>
                    <td>
                      {f.webViewLink ? (
                        <a class="doc-link" href={f.webViewLink} target="_blank" rel="noreferrer">{f.name}</a>
                      ) : f.name}
                    </td>
                    <td>{f.modifiedTime ? formatDate(f.modifiedTime) : EM_DASH}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </section>
  );
}
